import { onAuthStateChanged } from 'firebase/auth';
import { resolvedAuthUser } from './resolvedAuthUser';
import { shouldRefreshExpiredToken } from '../components/notifications/notificationResponseErrors';
import { auth } from '../services/firebaseAuth';
import { backendApiUrl } from '../config/backendApiUrl';
import { API_ROUTES } from '../shared/apiRoutes/apiRoutes';
import { applySupportTenantToUrl, requestContextHeaders, supportHeadersForEndpoint } from '../pages/Admin/supportedTenantsSession';

// Base URL for API calls (using relative paths with Firebase Hosting rewrites)
const BASE_URL = ''; // Empty string means use relative paths

// Request deduplication tracking
const pendingRequests = new Map();
const requestTimestamps = new Map();
const REQUEST_COOLDOWN = 1000; // 1 second cooldown between identical requests

// Feature support caches
// Cache whether tenant SMS config route is unsupported to avoid repeated 404s in-session
const smsConfigSupportCache = new Map(); // tenantId -> 'unsupported' | 'ok'

// Helper to get ID token with deduplication
const getIdToken = async (forceRefresh = false, knownUser = null) => {
  const user = knownUser || await resolvedAuthUser();

  if (!user) {
    if (import.meta.env?.DEV) console.debug('[API] No authenticated user for token');
    return null;
  }

  // Check if we're already getting a token for this user
  const tokenKey = `token_${user.uid}`;
  if (pendingRequests.has(tokenKey)) {
    if (import.meta.env?.DEV) console.debug(`[API] Duplicate token request for ${user.uid} skipped`);
    return pendingRequests.get(tokenKey);
  }

  // Check cooldown period
  const now = Date.now();
  const lastRequest = requestTimestamps.get(tokenKey) || 0;
  const timeSinceLastRequest = now - lastRequest;

  if (timeSinceLastRequest < REQUEST_COOLDOWN && !forceRefresh) {
    if (import.meta.env?.DEV) {
      console.debug(
        `[API] Token request cooldown (${Math.floor(timeSinceLastRequest)}ms < ${REQUEST_COOLDOWN}ms)`
      );
    }
    // Use cached token
    try {
      return await user.getIdToken(false);
    } catch (error) {
      console.error(`[API] 🔴 Error getting cached token: ${error.message}`);
      // Fall through to force refresh
    }
  }

  // Create a new promise for this token request
  const tokenPromise = (async () => {
    try {
      if (import.meta.env?.DEV) {
        console.debug(`[API] Getting ID token (forceRefresh=${forceRefresh}) for ${user.uid}`);
      }
      const startTime = performance.now();
      const token = await user.getIdToken(forceRefresh);
      const duration = Math.floor(performance.now() - startTime);

      if (import.meta.env?.DEV) console.debug(`[API] Token obtained in ${duration}ms`);
      requestTimestamps.set(tokenKey, Date.now());
      return token;
    } catch (error) {
      console.error(`[API] 🔴 Error getting ID token: ${error.message}`);
      throw error;
    } finally {
      // Remove from pending requests
      pendingRequests.delete(tokenKey);
    }
  })();

  // Store the promise
  pendingRequests.set(tokenKey, tokenPromise);
  return tokenPromise;
};

 

// Generic fetch helper with authorization and deduplication
// opts: { suppressErrorLog?: boolean }
export const fetchWithAuth = async (endpoint, method = 'GET', body = null, opts = {}) => {
  // Generate a unique request key based on endpoint, method, and body
  const bodyStr = body ? JSON.stringify(body) : '';
  const requestKey = `${method}_${endpoint}_${opts?.supportTenantId || ''}_${bodyStr.substring(0, 50)}`;

  // Check if we have a pending identical request
  if (pendingRequests.has(requestKey)) {
    if (import.meta.env?.DEV) console.debug(`[API] Duplicate request skipped: ${method} ${endpoint}`);
    return pendingRequests.get(requestKey);
  }

  // Check cooldown period
  const now = Date.now();
  const lastRequest = requestTimestamps.get(requestKey) || 0;
  const timeSinceLastRequest = now - lastRequest;

  if (timeSinceLastRequest < REQUEST_COOLDOWN) {
    if (import.meta.env?.DEV) {
      console.debug(
        `[API] Request cooldown: ${method} ${endpoint} (${Math.floor(timeSinceLastRequest)}ms < ${REQUEST_COOLDOWN}ms)`
      );
    }
  }

  // Create a new promise for this request
  const requestPromise = (async () => {
    try {
      // Get auth token with deduplication
      const token = await getIdToken(Boolean(opts?.forceTokenRefresh), opts?.authUser || null);
      if (!token) {
        throw new Error('User not authenticated');
      }

      const options = {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...requestContextHeaders(opts?.supportTenantId),
          ...supportHeadersForEndpoint(endpoint),
        },
      };

      if (body) {
        options.body = JSON.stringify(body);
      }

      // Standardize endpoint format to always use /api/ prefix
      let url = applySupportTenantToUrl(endpoint);

      // Check if this is a legacy endpoint (not starting with /api/)
      if (!endpoint.startsWith('/api/')) {
        // Format: Convert any endpoint to /api/... format
        url = `/api${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;

        // Add warning for legacy endpoint access (dev only)
        if (import.meta.env?.DEV && !(opts?.silent || opts?.suppressRequestLog)) {
          console.warn(`[API] Legacy endpoint detected: ${endpoint} -> ${url}`);
        }
      }
      // Request logging (dev only)
      if (import.meta.env?.DEV && !(opts?.silent || opts?.suppressRequestLog)) {
        console.debug(`[API] Request: ${method} ${url}`);
      }

      const startTime = performance.now();
      const response = await fetch(backendApiUrl(url), options);
      const duration = Math.floor(performance.now() - startTime);
      const ct = response.headers.get('content-type') || '';
      const text = await response.text();

      // Response logging (dev only)
      if (import.meta.env?.DEV && !(opts?.silent || opts?.suppressResponseLog)) {
        console.debug(`[API] Response: ${method} ${url} (${duration}ms, ${response.status})`);
      }

      // Update request timestamp
      requestTimestamps.set(requestKey, Date.now());

      if (!response.ok) {
        const error = new Error(`API Error (${response.status}): ${text}`);
        error.status = response.status;
        try {
          error.body = JSON.parse(text);
          error.code = error.body?.code || error.body?.error_code || '';
        } catch {
          error.body = null;
          error.code = '';
        }
        throw error;
      }

      if (!ct.includes('application/json')) {
        throw new Error(`Invalid JSON response from ${url}: ${text}`);
      }

      return JSON.parse(text);
    } catch (error) {
      if (!(opts?.suppressErrorLog || opts?.silent)) {
        console.error(`[API] 🔴 Error in ${method} ${endpoint}:`, error);
      }
      throw error;
    } finally {
      // Remove from pending requests
      pendingRequests.delete(requestKey);
    }
  })();

  // Store the promise
  pendingRequests.set(requestKey, requestPromise);
  return requestPromise;
};

export const callAtURL = fetchWithAuth;

// Helper to safely build URLs with query parameters (avoids manual string concatenation)
const buildUrlWithQuery = (basePath, params = {}) => {
  const [path, existingQuery = ''] = basePath.split('?');
  const search = new URLSearchParams(existingQuery);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).length > 0) {
      search.set(key, value);
    }
  });
  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
};

// Debug endpoint to check auth state
export const debugAuth = async () => {
  try {
    const user = auth.currentUser;

    if (!user) {
      return { authenticated: false, message: 'No authenticated user' };
    }

    // Get token without force refresh
    const token = await getIdToken(false);

    // Get token details
    const tokenDetails = await user.getIdTokenResult();

    return {
      authenticated: true,
      uid: user.uid,
      email: user.email,
      provider: user.providerData[0]?.providerId || 'unknown',
      claims: tokenDetails.claims,
      tokenExpiration: new Date(tokenDetails.expirationTime).toISOString(),
      lastLogin: new Date(user.metadata.lastSignInTime).toISOString(),
      timestamp: new Date().toISOString(),
      pendingRequests: Array.from(pendingRequests.keys()),
      requestHistory: Array.from(requestTimestamps.entries()).map(([key, time]) => ({
        request: key,
        lastRequestTime: new Date(time).toISOString(),
        msSinceLastRequest: Date.now() - time,
      })),
    };
  } catch (error) {
    console.error('[API] 🔴 Error in debugAuth:', error);
    return {
      authenticated: false,
      error: error.message,
      timestamp: new Date().toISOString(),
    };
  }
};

// API Helper Functions
export const getUsers = async () => {
  const response = await fetchWithAuth(API_ROUTES.USERS_LIST);
  console.log('🔍 [backendApi] getUsers response:', response);

  // Handle the new response format with data and success fields
  if (response && response.success && Array.isArray(response.data)) {
    return response.data;
  }

  // Support legacy formats
  if (Array.isArray(response)) {
    return response;
  }

  return Array.isArray(response.users) ? response.users : [];
};

export const listUserDirectory = () =>
  fetchWithAuth(API_ROUTES.USERS_DIRECTORY, 'GET', null, { silent: true });

export const searchTenantUsersAdmin = async (tenantId, query = '', limit = 50) => {
  if (!tenantId) return [];
  const endpoint = buildUrlWithQuery(API_ROUTES.USERS_SEARCH_ADMIN, {
    tenantId,
    q: query || '',
    limit,
  });
  const resp = await fetchWithAuth(endpoint, 'GET', null, { silent: true });
  if (resp && resp.data && Array.isArray(resp.data.users)) return resp.data.users;
  return [];
};

export const addTenant = async tenantData => {
  return fetchWithAuth(API_ROUTES.TENANTS_ADD, 'POST', tenantData);
};

export const sendAlert = async alertData => {
  return fetchWithAuth(API_ROUTES.ALERTS_SEND, 'POST', alertData);
};

export const sendWebhook = async webhookData => {
  return fetchWithAuth(API_ROUTES.WEBHOOKS_SEND, 'POST', webhookData);
};

export const getTenants = async ({ includeInactive = false } = {}) => {
  try {
    // Use non-admin v2 user-tenants route
    const endpoint = includeInactive
      ? `${API_ROUTES.TENANTS_USER_LIST}?includeInactive=true`
      : API_ROUTES.TENANTS_USER_LIST;
    if (process.env.NODE_ENV !== 'production') {
      console.log(`[API] Tenants: GET ${endpoint}`);
    }
    const response = await fetchWithAuth(endpoint);
    if (process.env.NODE_ENV !== 'production') {
      console.log('[API] Tenants: response', response);
    }

    // Normalize shapes: array | { data: [] } | { tenants: [] }
    if (Array.isArray(response)) return response;
    if (response && Array.isArray(response.data)) return response.data;
    if (response && Array.isArray(response.tenants)) return response.tenants;

    return [];
  } catch (error) {
    // If the canonical route is not yet available, fall back to admin list for admin users
    const msg = String(error?.message || '');
    if (msg.includes('API Error (404)')) {
      try {
        const adminList = await getTenantsAdminList();
        if (Array.isArray(adminList)) return adminList;
        if (adminList && Array.isArray(adminList.data)) return adminList.data;
        if (adminList && adminList.data && Array.isArray(adminList.data.items)) return adminList.data.items;
      } catch (err2) {
        console.warn('[API] Tenants admin list fallback failed:', err2);
      }
    }
    console.error('Error fetching tenants:', error);
    throw error;
  }
};

/**
 * Get all tenants (admin v2)
 * Uses canonical admin route to list all tenants for administration UIs
 * @returns {Promise<Array<{id: string, name?: string}>>}
 */
export const getTenantsAdminList = async () => {
  try {
    const endpoint = API_ROUTES.TENANTS_LIST; // '/api/v2/admin/tenants/list'
    if (process.env.NODE_ENV !== 'production') {
      console.log(`[API] Admin Tenants: GET ${endpoint}`);
    }
    const response = await fetchWithAuth(endpoint);
    if (process.env.NODE_ENV !== 'production') {
      console.log('[API] Admin Tenants: response', response);
    }

    // Expected v2 shape: { success, data: { items: [...] } } or { success, data: [...] }
    if (response && Array.isArray(response)) return response;
    if (response && Array.isArray(response.data)) return response.data;
    if (response && response.data && Array.isArray(response.data.items)) return response.data.items;
    if (response && Array.isArray(response.tenants)) return response.tenants;

    return [];
  } catch (error) {
    console.error('Error fetching admin tenants list:', error);
    throw error;
  }
};

/**
 * Get a tenant by ID (non-admin v2)
 * @param {string} tenantId
 * @returns {Promise<Object>} - { success, data, request_id }
 */
export const getTenantById = async (tenantId) => {
  const endpoint = API_ROUTES.TENANT_GET.replace(':tenantId', tenantId);
  return fetchWithAuth(endpoint);
};

/**
 * Get tenant details/statistics (non-admin v2)
 * @param {string} tenantId
 * @returns {Promise<Object>} - { success, data: { statistics, tenant }, request_id }
 */
export const getTenantDetails = async (tenantId) => {
  const endpoint = API_ROUTES.TENANT_DETAILS.replace(':tenantId', tenantId);
  return fetchWithAuth(endpoint);
};

const __tenantAgentsHealthCache = new Map();

export const getTenantAgentsHealth = async (tenantId, options = {}) => {
  const maxAgeMs = Number.isFinite(options?.maxAgeMs) ? options.maxAgeMs : 30000;
  const force = !!options?.force;

  const cacheKey = String(tenantId || '');
  if (!cacheKey) {
    return { success: false, data: { tenantId: tenantId || '', agents: [], count: 0 } };
  }

  const cached = __tenantAgentsHealthCache.get(cacheKey);
  const now = Date.now();
  if (!force && cached && typeof cached.fetchedAt === 'number' && (now - cached.fetchedAt) < maxAgeMs) {
    return cached.value;
  }

  const endpoint = API_ROUTES.TENANT_AGENTS_HEALTH.replace(':tenantId', cacheKey);
  const resp = await fetchWithAuth(endpoint, 'GET', null, { silent: true });
  __tenantAgentsHealthCache.set(cacheKey, { fetchedAt: now, value: resp });
  return resp;
};

/**
 * Log a tenant switch event (non-admin v2)
 * @param {string} newTenantId - required
 * @param {string} [previousTenantId] - optional
 * @returns {Promise<Object>} - { success, data: { userId, previousTenantId, newTenantId, timestamp }, request_id }
 */
export const logTenantSwitch = async (newTenantId, previousTenantId = undefined) => {
  const payload = previousTenantId ? { newTenantId, previousTenantId } : { newTenantId };
  return fetchWithAuth(API_ROUTES.TENANT_SWITCH_LOG, 'POST', payload);
};

/**
 * List memberships for the current authenticated user
 * @param {Object} [options]
 * @param {boolean} [options.includeHidden]
 * @param {boolean} [options.includeRemoved]
 * @returns {Promise<Array<Object>>}
 */
export const getMyMemberships = async (options = {}) => {
  const params = {};
  if (options.includeHidden) params.includeHidden = 'true';
  if (options.includeRemoved) params.includeRemoved = 'true';
  const endpoint = buildUrlWithQuery(API_ROUTES.USERS_ME_MEMBERSHIPS, params);
  const resp = await fetchWithAuth(endpoint, 'GET', null, { silent: true });

  if (resp && resp.success && resp.data && Array.isArray(resp.data.memberships)) {
    return resp.data.memberships;
  }
  if (resp && Array.isArray(resp.memberships)) return resp.memberships;
  return [];
};

/**
 * List memberships for a specific tenant
 * @param {string} tenantId
 * @param {Object} [options]
 * @param {boolean} [options.includeHidden]
 * @param {boolean} [options.includeRemoved]
 * @param {string[]} [options.roles]
 * @returns {Promise<Array<Object>>}
 */
export const getTenantMemberships = async (tenantId, options = {}) => {
  if (!tenantId) return [];
  const params = {};
  if (options.includeHidden) params.includeHidden = 'true';
  if (options.includeRemoved) params.includeRemoved = 'true';
  if (options.roles && Array.isArray(options.roles) && options.roles.length) {
    params.roles = options.roles.join(',');
  }
  const path = API_ROUTES.TENANT_MEMBERSHIPS.replace(':tenantId', tenantId);
  const endpoint = buildUrlWithQuery(path, params);
  const resp = await fetchWithAuth(endpoint, 'GET', null, { silent: true });

  if (resp && resp.success && resp.data && Array.isArray(resp.data.memberships)) {
    return resp.data.memberships;
  }
  if (resp && Array.isArray(resp.memberships)) return resp.memberships;
  return [];
};

/**
 * List SIP-capable users for a specific tenant based on memberships
 * @param {string} tenantId
 * @param {Object} [options]
 * @param {boolean} [options.includeRemoved]
 * @returns {Promise<Array<Object>>}
 */
export const getTenantSipUsers = async (tenantId, options = {}) => {
  if (!tenantId) return [];
  const params = {};
  if (options.includeRemoved) params.includeRemoved = 'true';
  const path = API_ROUTES.TENANT_SIP_USERS.replace(':tenantId', tenantId);
  const endpoint = buildUrlWithQuery(path, params);
  const resp = await fetchWithAuth(endpoint, 'GET', null, { silent: true });

  if (resp && resp.success && resp.data && Array.isArray(resp.data.users)) {
    return resp.data.users;
  }
  if (resp && Array.isArray(resp.users)) return resp.users;
  return [];
};

/**
 * Admin: Update a specific user's membership in a tenant
 * @param {string} tenantId
 * @param {string} userId
 * @param {Object} payload - { role?, visible?, hasSipIdentity?, extension?, status? }
 * @returns {Promise<Object>} - { success, data: { tenantId, userId, membership }, request_id }
 */
export const updateTenantMembershipAdmin = async (tenantId, userId, payload) => {
  const path = API_ROUTES.TENANT_MEMBERSHIP_UPDATE_ADMIN
    .replace(':tenantId', tenantId)
    .replace(':userId', userId);
  return fetchWithAuth(path, 'PATCH', payload, { silent: true });
};

export const updateUser = async (userId, userData) => {
  return fetchWithAuth(`/api/users/${userId}/update`, 'POST', userData);
};

export const deleteUser = async userId => {
  return fetchWithAuth(`/api/users/${userId}/delete`, 'DELETE');
};

export const getAlerts = async () => {
  return fetchWithAuth(API_ROUTES.ALERTS_LIST);
};

export const updateTenant = async (tenantId, tenantData) => {
  const endpoint = API_ROUTES.TENANTS_UPDATE.replace(':tenantId', tenantId);
  return fetchWithAuth(endpoint, 'PATCH', tenantData);
};

export const deleteTenant = async tenantId => {
  const endpoint = API_ROUTES.TENANTS_DELETE.replace(':tenantId', tenantId);
  return fetchWithAuth(endpoint, 'DELETE');
};

// Claims Management API
/**
 * Set custom claims for a user
 * @param {string} userId - User ID to update
 * @param {Object} claims - Claims to set
 * @returns {Promise<Object>} - API response
 */
export const setUserClaims = async (userId, claims) => {
  return fetchWithAuth(API_ROUTES.USERS_SET_CLAIMS, 'POST', { uid: userId, claims });
};

/**
 * Add a tenant to a user
 * @param {string} userId - User ID to update
 * @param {string} tenantId - Tenant ID to add
 * @returns {Promise<Object>} - API response
 */
export const addTenantToUser = async (userId, tenantId) => {
  // Import events dynamically to avoid circular dependencies
  const { publish, EVENTS } = await import('../utils/events');

  const response = await fetchWithAuth(API_ROUTES.USERS_ADD_TENANT, 'POST', { uid: userId, tenantId });
  // Force refresh of the user data in cache to ensure tenantId is updated
  // This helps prevent stale tenantId data in the UI
  try {
    // Clear any cached user data to ensure fresh data on next fetch
    const cacheKey = `users_${userId}`;
    if (pendingRequests.has(cacheKey)) {
      pendingRequests.delete(cacheKey);
    }
    if (requestTimestamps.has(cacheKey)) {
      requestTimestamps.delete(cacheKey);
    }

    // Publish event to notify components that tenant assignment has changed
    publish(EVENTS.USER_TENANT_UPDATED, { userId, tenantId });
    // Also publish a general user list refresh event
    publish(EVENTS.USER_LIST_REFRESH, null);

    console.log('[API] 📣 Published user tenant update event', { userId, tenantId });
  } catch (err) {
    console.warn('[API] Failed to clear user cache or publish event after tenant assignment:', err);
  }

  return response;
};

/**
 * Remove a tenant from a user
 * @param {string} userId - User ID to update
 * @param {string} tenantId - Tenant ID to remove
 * @returns {Promise<Object>} - API response
 */
export const removeTenantFromUser = async (userId, tenantId) => {
  return fetchWithAuth(API_ROUTES.USERS_REMOVE_TENANT, 'POST', { uid: userId, tenantId });
};

// New API functions for migration

/**
 * Create a new event
 * @param {Object} eventData - Event data to create
 * @returns {Promise<Object>} - API response
 */
export const createEvent = async eventData => {
  return fetchWithAuth(API_ROUTES.EVENT_NEW, 'POST', eventData);
};

/**
 * Fetch events for a specific tenant
 * @param {string} tenantId - Tenant ID to fetch events for
 * @returns {Promise<Object>} - API response with events list
 */
export const listenToEvents = async tenantId => {
  const endpoint = buildUrlWithQuery('/api/events/list', { tenantId });
  return fetchWithAuth(endpoint);
};

/**
 * Fetch users from authentication service
 * @param {Object} options - Optional parameters
 * @param {string} options.tenantId - Optional tenant ID to filter users
 * @returns {Promise<Object>} - API response with users list
 */
export const fetchUsersFromAuth = async (options = {}) => {
  const queryParams = new URLSearchParams();
  if (options.tenantId) queryParams.append('tenantId', options.tenantId);
  const query = queryParams.toString() ? `?${queryParams}` : '';
  console.log(`[API] Fetching users with query: ${query}`);
  // Use non-admin users list endpoint so both admin and tenant_admin roles can fetch
  // the full tenant user list for features like RTP/SIP paging name resolution.
  // This maps to handlers.user_handlers.list_users at /api/users/list.
  return fetchWithAuth(`/api/users/list${query}`);
};

/**
 * Update a user's information
 * @param {string} userId - User ID to update
 * @param {Object} updates - User data updates
 * @returns {Promise<Object>} - API response
 */
export const updateUserInfo = async (userId, updates) => {
  return fetchWithAuth(`/api/users/update/${userId}`, 'PATCH', updates);
};

export const listTenantSupportAccess = async (userId) => {
  return fetchWithAuth(`/api/v2/admin/users/${encodeURIComponent(userId)}/tenant-support`);
};

export const saveTenantSupportAccess = async (userId, tenantId, body) => {
  return fetchWithAuth(
    `/api/v2/admin/users/${encodeURIComponent(userId)}/tenant-support/${encodeURIComponent(tenantId)}`,
    'PUT',
    body,
  );
};

export const revokeTenantSupportAccess = async (userId, tenantId, body) => {
  return fetchWithAuth(
    `/api/v2/admin/users/${encodeURIComponent(userId)}/tenant-support/${encodeURIComponent(tenantId)}`,
    'DELETE',
    body,
  );
};

export const discoverTenantSupport = async () => {
  return fetchWithAuth('/api/v2/me/tenant-support');
};

export const loadSupportedTenant = async (tenantId) => {
  return fetchWithAuth(
    `/api/v2/support/tenants/${encodeURIComponent(tenantId)}`,
    'GET',
    null,
    { supportTenantId: tenantId },
  );
};

export const saveSupportedTenantSettings = async (tenantId, body) => {
  return fetchWithAuth(
    `/api/v2/support/tenants/${encodeURIComponent(tenantId)}`,
    'PATCH',
    body,
    { supportTenantId: tenantId },
  );
};

/**
 * Dispatch an action
 * @param {Object} actionData - Action data to dispatch
 * @returns {Promise<Object>} - API response
 */
export const dispatchAction = async actionData => {
  return fetchWithAuth(API_ROUTES.ACTIONS_DISPATCH, 'POST', actionData);
};

/**
 * Get event templates
 * @param {string} tenantId - Tenant ID to fetch templates for
 * @returns {Promise<Object>} - API response with templates
 */
export const getEventTemplates = async tenantId => {
  // Prefer canonical templates route
  const endpoint = buildUrlWithQuery(API_ROUTES.EVENT_TEMPLATES, { tenantId });
  return fetchWithAuth(endpoint);
};

/**
 * Get event flows
 * @param {string} tenantId - Tenant ID to fetch flows for
 * @returns {Promise<Object>} - API response with flows
 */
export const getEventFlows = async (tenantId, options = {}) => {
  // Prefer canonical flows route
  const { includeAssignments } = options;
  const endpoint = buildUrlWithQuery(API_ROUTES.FLOWS, {
    tenantId,
    // Backend expects includeAssignments in query to include assigned user IDs
    includeAssignments: includeAssignments ? 'true' : undefined,
  });
  return fetchWithAuth(endpoint);
};

/**
 * Create a new event flow
 * @param {Object} flowData - Flow data to create
 * @returns {Promise<Object>} - API response
 */
export const createEventFlow = async flowData => {
  return fetchWithAuth(API_ROUTES.EVENT_FLOWS_CREATE, 'POST', flowData);
};

/**
 * Update an event flow
 * @param {string} flowId - Flow ID to update
 * @param {Object} flowData - Updated flow data
 * @returns {Promise<Object>} - API response
 */
export const updateEventFlow = async (flowId, flowData) => {
  return fetchWithAuth(`/api/event-flows/${flowId}/update`, 'POST', flowData);
};

/**
 * Delete an event flow
 * @param {string} flowId - Flow ID to delete
 * @returns {Promise<Object>} - API response
 */
export const deleteEventFlow = async flowId => {
  return fetchWithAuth(`/event-flows/${flowId}/delete`, 'DELETE');
};

// Flow Assignments
/**
 * Assign one or more flows to a user within a tenant
 * @param {Object} params
 * @param {string} params.tenantId
 * @param {string} params.userId
 * @param {string[]|string} params.flowIds
 * @returns {Promise<Object>} - { success, assigned, request_id }
 */
export const assignFlowsToUser = async ({ tenantId, userId, flowIds }) => {
  const body = { tenantId, userId, flowIds };
  return fetchWithAuth(API_ROUTES.FLOWS_ASSIGN_ADMIN, 'POST', body);
};

/**
 * Unassign one or more flows from a user within a tenant
 * @param {Object} params
 * @param {string} params.tenantId
 * @param {string} params.userId
 * @param {string[]|string} params.flowIds
 * @returns {Promise<Object>} - { success, unassigned, request_id }
 */
export const unassignFlowsFromUser = async ({ tenantId, userId, flowIds }) => {
  const body = { tenantId, userId, flowIds };
  return fetchWithAuth(API_ROUTES.FLOWS_UNASSIGN_ADMIN, 'POST', body);
};

/**
 * List flow assignments for a specific user
 * @param {string} userId
 * @param {string} tenantId
 * @param {boolean} [includeFlowData=false]
 * @returns {Promise<Object>} - { success, data: { userId, flowIds, flows? }, request_id }
 */
export const getFlowAssignmentsByUser = async (userId, tenantId, includeFlowData = false) => {
  const path = API_ROUTES.FLOWS_ASSIGNMENTS_BY_USER_ADMIN.replace(':userId', userId);
  const endpoint = buildUrlWithQuery(path, {
    tenantId,
    includeFlowData: includeFlowData ? 'true' : undefined,
  });
  return fetchWithAuth(endpoint);
};

export const getFlowAssignmentsByUserPublic = async (userId, tenantId, includeFlowData = false) => {
  const path = API_ROUTES.FLOWS_ASSIGNMENTS_BY_USER.replace(':userId', userId);
  const endpoint = buildUrlWithQuery(path, {
    tenantId,
    includeFlowData: includeFlowData ? 'true' : undefined,
  });
  return fetchWithAuth(endpoint);
};

/**
 * List user assignments for a specific flow
 * @param {string} flowId
 * @param {string} tenantId
 * @param {boolean} [includeUserData=false]
 * @returns {Promise<Object>} - { success, data: { flowId, userIds, users? }, request_id }
 */
export const getFlowAssignmentsByFlow = async (flowId, tenantId, includeUserData = false) => {
  const path = API_ROUTES.FLOWS_ASSIGNMENTS_BY_FLOW_ADMIN.replace(':flowId', flowId);
  const endpoint = buildUrlWithQuery(path, {
    tenantId,
    includeUserData: includeUserData ? 'true' : undefined,
  });
  return fetchWithAuth(endpoint);
};

/**
 * Acknowledge an alert or update its status
 * @param {string} alertId - The alert ID to update
 * @param {Object} updateData - Data for the update including status
 * @returns {Promise<Object>} - API response
 */
export const acknowledgeAlert = async (alertId, updateData) => {
  return fetchWithAuth(`/alerts/${alertId}/update`, 'POST', updateData);
};

/**
 * Get active alerts for responder
 * @param {string} status - Optional status filter
 * @returns {Promise<Array>} - Array of alerts
 */
export const getActiveAlerts = async status => {
  let endpoint = '/alerts/active';
  if (status) {
    endpoint += `?status=${status}`;
  }
  return fetchWithAuth(endpoint);
};

// Tenant SMS Config (Admin)
/**
 * Get tenant SMS configuration (admin v2)
 * @param {string} tenantId
 * @returns {Promise<Object>} - API response (may be { success, data } or raw object)
 */
export const getTenantSmsConfigAdmin = async (tenantId, opts = {}) => {
  const endpoint = API_ROUTES.TENANT_SMS_CONFIG_ADMIN.replace(':tenantId', tenantId);
  // Fast path: if we already detected unsupported for this tenant, skip network call
  // unless explicitly forced
  if (!opts?.force && smsConfigSupportCache.get(tenantId) === 'unsupported') {
    return { __unsupported: true };
  }
  try {
    const resp = await fetchWithAuth(endpoint, 'GET', null, { silent: true });
    // Mark as supported on success so we don't short-circuit next time
    try { smsConfigSupportCache.set(tenantId, 'ok'); } catch (_) {}
    return resp;
  } catch (err) {
    // Be resilient: backend route may not exist yet in some environments.
    console.warn('[API] Tenant SMS config fetch failed, returning empty config:', err);
    smsConfigSupportCache.set(tenantId, 'unsupported');
    return { __unsupported: true };
  }
};

/**
 * Update tenant SMS configuration (admin v2)
 * @param {string} tenantId
 * @param {Object} payload - { mode, status, fallbackSharedNumber, ... }
 * @returns {Promise<Object>} - API response
 */
export const updateTenantSmsConfigAdmin = async (tenantId, payload) => {
  const endpoint = API_ROUTES.TENANT_SMS_CONFIG_ADMIN.replace(':tenantId', tenantId);
  try {
    return await fetchWithAuth(endpoint, 'PATCH', payload, { silent: true });
  } catch (err) {
    const msg = String(err?.message || '');
    // Try a non-admin fallback route if available
    if (msg.includes('API Error (404)')) {
      const fallback = `/api/v2/tenants/${tenantId}/sms-config`;
      try {
        return await fetchWithAuth(fallback, 'PATCH', payload, { silent: true });
      } catch (err2) {
        const msg2 = String(err2?.message || '');
        if (msg2.includes('API Error (404)')) {
          const unsupported = new Error('SMS config update route is not available in this environment');
          unsupported.code = 'UNSUPPORTED';
          throw unsupported;
        }
        throw err2;
      }
    }
    throw err;
  }
};

export const getTenantPagingSettingsAdmin = async (tenantId) => {
  const endpoint = API_ROUTES.TENANT_PAGING_SETTINGS_ADMIN.replace(':tenantId', tenantId);
  const resp = await fetchWithAuth(endpoint, 'GET', null, { silent: true });
  return (resp && resp.data && resp.data.settings) || {};
};

export const upsertTenantPagingSettingsAdmin = async (tenantId, payload) => {
  const endpoint = API_ROUTES.TENANT_PAGING_SETTINGS_ADMIN.replace(':tenantId', tenantId);
  const resp = await fetchWithAuth(endpoint, 'PUT', payload, { silent: true });
  return (resp && resp.data && resp.data.settings) || {};
};

export const getTenantPagingRolePoliciesAdmin = async (tenantId) => {
  const endpoint = API_ROUTES.TENANT_PAGING_ROLE_POLICIES_ADMIN.replace(':tenantId', tenantId);
  const resp = await fetchWithAuth(endpoint, 'GET', null, { silent: true });
  return (resp && resp.data && resp.data.roles) || {};
};

export const setTenantPagingRolePoliciesAdmin = async (tenantId, roles) => {
  const endpoint = API_ROUTES.TENANT_PAGING_ROLE_POLICIES_ADMIN.replace(':tenantId', tenantId);
  const body = { roles: roles || {} };
  const resp = await fetchWithAuth(endpoint, 'PUT', body, { silent: true });
  return (resp && resp.data && resp.data.roles) || {};
};

export const getTenantSegmentsAdmin = async (tenantId) => {
  const endpoint = `${API_ROUTES.SEGMENTS_ADMIN}?tenantId=${tenantId}`;
  console.log('[API] Fetching segments with endpoint:', endpoint);
  const resp = await fetchWithAuth(endpoint, 'GET', null, { silent: true });
  console.log('[API] Segments response:', resp);
  if (resp && resp.data && Array.isArray(resp.data.segments)) {
    console.log('[API] Returning segments:', resp.data.segments);
    return resp.data.segments;
  }
  console.log('[API] No segments found, returning empty array');
  return [];
};

export const getTenantPagingZonesAdmin = async (tenantId) => {
  const endpoint = API_ROUTES.TENANT_PAGING_ZONES_ADMIN.replace(':tenantId', tenantId);
  const resp = await fetchWithAuth(endpoint, 'GET', null, { silent: true });
  if (resp && resp.data && Array.isArray(resp.data.zones)) return resp.data.zones;
  return [];
};

export const createTenantPagingZoneAdmin = async (tenantId, payload) => {
  const endpoint = API_ROUTES.TENANT_PAGING_ZONES_ADMIN.replace(':tenantId', tenantId);
  const resp = await fetchWithAuth(endpoint, 'POST', payload, { silent: true });
  return (resp && resp.data && resp.data.zone) || null;
};

export const updateTenantPagingZoneAdmin = async (tenantId, zoneId, payload) => {
  const endpoint = API_ROUTES.TENANT_PAGING_ZONE_ADMIN
    .replace(':tenantId', tenantId)
    .replace(':zoneId', zoneId);
  const resp = await fetchWithAuth(endpoint, 'PUT', payload, { silent: true });
  return (resp && resp.data && resp.data.zone) || null;
};

export const deleteTenantPagingZoneAdmin = async (tenantId, zoneId) => {
  const endpoint = API_ROUTES.TENANT_PAGING_ZONE_ADMIN
    .replace(':tenantId', tenantId)
    .replace(':zoneId', zoneId);
  return fetchWithAuth(endpoint, 'DELETE', null, { silent: true });
};

export const getTenantPagingZoneDevicesAdmin = async (tenantId, zoneId) => {
  const endpoint = API_ROUTES.TENANT_PAGING_ZONE_DEVICES_ADMIN
    .replace(':tenantId', tenantId)
    .replace(':zoneId', zoneId);
  const resp = await fetchWithAuth(endpoint, 'GET', null, { silent: true });
  if (resp && resp.data && Array.isArray(resp.data.devices)) return resp.data.devices;
  return [];
};

export const setTenantPagingZoneDevicesAdmin = async (tenantId, zoneId, deviceIds) => {
  const endpoint = API_ROUTES.TENANT_PAGING_ZONE_DEVICES_ADMIN
    .replace(':tenantId', tenantId)
    .replace(':zoneId', zoneId);
  const body = { deviceIds: Array.isArray(deviceIds) ? deviceIds : [] };
  return fetchWithAuth(endpoint, 'PUT', body, { silent: true });
};

export const getTenantPagingDeviceZonesAdmin = async (tenantId, deviceId) => {
  const endpoint = API_ROUTES.TENANT_PAGING_DEVICE_ZONES_ADMIN
    .replace(':tenantId', tenantId)
    .replace(':deviceId', deviceId);
  const resp = await fetchWithAuth(endpoint, 'GET', null, { silent: true });
  if (resp && resp.data && Array.isArray(resp.data.zoneIds)) return resp.data.zoneIds;
  return [];
};

export const setTenantPagingDeviceZonesAdmin = async (tenantId, deviceId, zoneIds) => {
  const endpoint = API_ROUTES.TENANT_PAGING_DEVICE_ZONES_ADMIN
    .replace(':tenantId', tenantId)
    .replace(':deviceId', deviceId);
  const body = { zoneIds: Array.isArray(zoneIds) ? zoneIds : [] };
  return fetchWithAuth(endpoint, 'PUT', body, { silent: true });
};

export const getTenantPagingMyZones = async (tenantId) => {
  const endpoint = API_ROUTES.TENANT_PAGING_MY_ZONES.replace(':tenantId', tenantId);
  const resp = await fetchWithAuth(endpoint, 'GET', null, { silent: true });
  if (resp && resp.data && Array.isArray(resp.data.zones)) return resp.data.zones;
  return [];
};

export const getTenantSipPagingSettingsAdmin = async (tenantId) => {
  const endpoint = API_ROUTES.TENANT_SIP_PAGING_SETTINGS_ADMIN.replace(':tenantId', tenantId);
  const resp = await fetchWithAuth(endpoint, 'GET', null, { silent: true });
  return (resp && resp.data && resp.data.settings) || {};
};

export const upsertTenantSipPagingSettingsAdmin = async (tenantId, payload) => {
  const endpoint = API_ROUTES.TENANT_SIP_PAGING_SETTINGS_ADMIN.replace(':tenantId', tenantId);
  const resp = await fetchWithAuth(endpoint, 'PUT', payload, { silent: true });
  return (resp && resp.data && resp.data.settings) || {};
};

export const getTenantSipPagingRolePoliciesAdmin = async (tenantId) => {
  const endpoint = API_ROUTES.TENANT_SIP_PAGING_ROLE_POLICIES_ADMIN.replace(':tenantId', tenantId);
  const resp = await fetchWithAuth(endpoint, 'GET', null, { silent: true });
  return (resp && resp.data && resp.data.roles) || {};
};

export const setTenantSipPagingRolePoliciesAdmin = async (tenantId, roles) => {
  const endpoint = API_ROUTES.TENANT_SIP_PAGING_ROLE_POLICIES_ADMIN.replace(':tenantId', tenantId);
  const body = { roles: roles || {} };
  const resp = await fetchWithAuth(endpoint, 'PUT', body, { silent: true });
  return (resp && resp.data && resp.data.roles) || {};
};

export const getTenantSipPagingZonesAdmin = async (tenantId) => {
  const endpoint = API_ROUTES.TENANT_SIP_PAGING_ZONES_ADMIN.replace(':tenantId', tenantId);
  const resp = await fetchWithAuth(endpoint, 'GET', null, { silent: true });
  if (resp && resp.data && Array.isArray(resp.data.zones)) return resp.data.zones;
  return [];
};

export const getTenantSipConfig = async (tenantId) => {
  if (!tenantId) return null;
  const endpoint = API_ROUTES.TENANT_SIP_CONFIG.replace(':tenantId', tenantId);
  try {
    const resp = await fetchWithAuth(endpoint, 'GET', null, { silent: true });
    if (resp && resp.success && resp.data) return resp.data;
    return resp?.data || null;
  } catch {
    return null;
  }
};

export const listTenantSipCredentials = async (tenantId) => {
  if (!tenantId) return [];
  const endpoint = API_ROUTES.TENANT_SIP_CREDENTIALS.replace(':tenantId', tenantId);
  const resp = await fetchWithAuth(endpoint, 'GET', null, { silent: true });
  return Array.isArray(resp?.data) ? resp.data : [];
};

export const createTenantSipCredential = async (tenantId, { username, label } = {}) => {
  if (!tenantId) throw new Error('Tenant is required');
  const endpoint = API_ROUTES.TENANT_SIP_CREDENTIALS.replace(':tenantId', tenantId);
  const resp = await fetchWithAuth(
    endpoint,
    'POST',
    { username, label: label || '' },
    { silent: true }
  );
  if (!resp?.success) {
    throw new Error(resp?.error || 'Failed to create SIP user in Twilio');
  }
  return resp.data || {};
};

export const deleteTenantSipCredential = async (tenantId, credentialSid, { username } = {}) => {
  if (!tenantId) throw new Error('Tenant is required');
  const sid = String(credentialSid || '').trim();
  const user = String(username || '').trim();
  const base = API_ROUTES.TENANT_SIP_CREDENTIALS.replace(':tenantId', tenantId);
  const endpoint = sid
    ? `${base}/${encodeURIComponent(sid)}`
    : `${base}/username/${encodeURIComponent(user)}`;
  if (!sid && !user) throw new Error('SIP user id is required');
  const resp = await fetchWithAuth(endpoint, 'DELETE', null, { silent: true });
  if (resp && resp.success === false) {
    throw new Error(resp.error || 'Failed to delete SIP user');
  }
  return resp;
};

export const resetTenantSipCredentialPassword = async (
  tenantId,
  { username, credentialSid } = {}
) => {
  if (!tenantId) throw new Error('Tenant is required');
  const sid = String(credentialSid || '').trim();
  const user = String(username || '').trim();

  const resetBySid = async (nextSid, nextUsername = user) => {
    const endpoint = API_ROUTES.TENANT_SIP_CREDENTIAL_SID_PASSWORD.replace(
      ':tenantId',
      tenantId
    ).replace(':credentialSid', encodeURIComponent(nextSid));
    const resp = await fetchWithAuth(endpoint, 'PUT', {}, { silent: true });
    if (!resp?.success || !resp?.data?.password) {
      throw new Error(resp?.error || 'Failed to generate a new SIP password');
    }
    return {
      username: nextUsername,
      credentialSid: nextSid,
      ...resp.data,
    };
  };

  if (sid) return resetBySid(sid, user);

  if (!user) throw new Error('SIP username is required');

  try {
    const endpoint = API_ROUTES.TENANT_SIP_CREDENTIAL_PASSWORD.replace(':tenantId', tenantId);
    const resp = await fetchWithAuth(endpoint, 'PUT', { username: user }, { silent: true });
    if (!resp?.success || !resp?.data?.password) {
      throw new Error(resp?.error || 'Failed to generate a new SIP password');
    }
    return { username: user, ...resp.data };
  } catch (error) {
    const text = String(error?.message || '');
    const missingRoute = /API Error \(404\)|API Error \(405\)/.test(text);
    if (!missingRoute) throw error;
  }

  const existing = await listTenantSipCredentials(tenantId);
  const match = (Array.isArray(existing) ? existing : []).find(
    (item) => String(item?.username || '').trim().toLowerCase() === user.toLowerCase()
  );
  const foundSid = String(match?.credentialSid || match?.id || '').trim();
  if (!foundSid) {
    throw new Error(
      `No Twilio SIP user is recorded for ${user}. Generate a password after this username exists in the tenant SIP credential list.`
    );
  }
  return resetBySid(foundSid, match?.username || user);
};

export const createTenantSipPagingZoneAdmin = async (tenantId, payload) => {
  const endpoint = API_ROUTES.TENANT_SIP_PAGING_ZONES_ADMIN.replace(':tenantId', tenantId);
  const resp = await fetchWithAuth(endpoint, 'POST', payload, { silent: true });
  return (resp && resp.data && resp.data.zone) || null;
};

export const updateTenantSipPagingZoneAdmin = async (tenantId, zoneId, payload) => {
  const endpoint = API_ROUTES.TENANT_SIP_PAGING_ZONE_ADMIN
    .replace(':tenantId', tenantId)
    .replace(':zoneId', zoneId);
  const resp = await fetchWithAuth(endpoint, 'PUT', payload, { silent: true });
  return (resp && resp.data && resp.data.zone) || null;
};

export const deleteTenantSipPagingZoneAdmin = async (tenantId, zoneId) => {
  const endpoint = API_ROUTES.TENANT_SIP_PAGING_ZONE_ADMIN
    .replace(':tenantId', tenantId)
    .replace(':zoneId', zoneId);
  return fetchWithAuth(endpoint, 'DELETE', null, { silent: true });
};

export const getTenantSipPagingMyZones = async (tenantId) => {
  const endpoint = API_ROUTES.TENANT_SIP_PAGING_MY_ZONES.replace(':tenantId', tenantId);
  const resp = await fetchWithAuth(endpoint, 'GET', null, { silent: true });
  if (resp && resp.data && Array.isArray(resp.data.zones)) return resp.data.zones;
  return [];
};

export const getMyImageDisplayActions = async (tenantId) => {
  const endpoint = API_ROUTES.TENANT_IMAGE_DISPLAY_MY_ACTIONS.replace(':tenantId', tenantId);
  const resp = await fetchWithAuth(endpoint, 'GET', null, { silent: true });
  if (resp && resp.data && Array.isArray(resp.data.actions)) return resp.data.actions;
  return [];
};

export const createImageDisplayUploadUrl = async ({ tenantId, filename, contentType }) => {
  const payload = { tenantId, filename, contentType };
  const resp = await fetchWithAuth(API_ROUTES.MEDIA_IMAGE_DISPLAY_UPLOAD_URL, 'POST', payload, { silent: true });
  return (resp && resp.data) || null;
};

export const isOption2ImageDisplayEnabled = () => {
  try {
    const raw = localStorage.getItem('useOption2ImageDisplay');
    if (raw === null || raw === undefined || String(raw).trim() === '') return true;
    const v = String(raw).trim().toLowerCase();
    if (v === 'true' || v === '1' || v === 'yes' || v === 'on') return true;
    if (v === 'false' || v === '0' || v === 'no' || v === 'off') return false;
    return true;
  } catch (_) {
    return true;
  }
};

export const dispatchImageDisplayCommand = async ({ tenantId, deviceId, imageUrl, filename, overlayText, devicePort, useHttps, clearAfterSeconds, segmentId, agentId }) => {
  const payload = {
    tenantId,
    deviceId,
    ...(segmentId ? { segmentId, segment_id: segmentId } : {}),
    ...(agentId ? { agentId, agent_id: agentId } : {}),
    imageUrl,
    filename,
    overlayText,
    ...(devicePort != null ? { devicePort } : {}),
    ...(useHttps != null ? { useHttps } : {}),
    ...(clearAfterSeconds != null ? { clearAfterSeconds } : {}),
  };
  return fetchWithAuth(API_ROUTES.MEDIA_IMAGE_DISPLAY_DISPATCH, 'POST', payload, { silent: true });
};

export const getTenantDevices = async (tenantId) => {
  if (!tenantId) return [];
  const endpoint = buildUrlWithQuery('/api/devices', { tenantId });
  const resp = await fetchWithAuth(endpoint, 'GET', null, { silent: true });
  if (resp && resp.networkEpoch != null && typeof window !== 'undefined') {
    const { dropDashboardCacheIfEpochChanged } = await import('../utils/networkCache');
    const { queryClient } = await import('../libs/queryClient');
    if (dropDashboardCacheIfEpochChanged(resp.networkEpoch, window.localStorage, window.sessionStorage)) {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    }
  }
  if (resp && Array.isArray(resp.data)) return resp.data;
  if (resp && resp.data && Array.isArray(resp.data.devices)) return resp.data.devices;
  return [];
};

// Test Tools
/**
 * Send a test SMS
 * @param {{ tenantId?: string, to: string, message: string }} params
 * @returns {Promise<Object>} - API response
 */
export const testSendSms = async ({ tenantId, to, message }) => {
  const body = { tenantId, to, message };
  return fetchWithAuth(API_ROUTES.TEST_SEND_SMS, 'POST', body);
};

// Tenant Voice Numbers (Admin)
export const getTenantVoiceNumbers = async (tenantId) => {
  const endpoint = API_ROUTES.TENANT_VOICE_NUMBERS_ADMIN.replace(':tenantId', tenantId);
  return fetchWithAuth(endpoint, 'GET');
};

export const createTenantVoiceNumber = async (tenantId, payload) => {
  const endpoint = API_ROUTES.TENANT_VOICE_NUMBERS_ADMIN.replace(':tenantId', tenantId);
  return fetchWithAuth(endpoint, 'POST', payload);
};

export const updateTenantVoiceNumber = async (tenantId, numId, payload) => {
  const endpoint = API_ROUTES.TENANT_VOICE_NUMBER_ADMIN
    .replace(':tenantId', tenantId)
    .replace(':numId', numId);
  return fetchWithAuth(endpoint, 'PATCH', payload);
};

export const deleteTenantVoiceNumber = async (tenantId, numId) => {
  const endpoint = API_ROUTES.TENANT_VOICE_NUMBER_ADMIN
    .replace(':tenantId', tenantId)
    .replace(':numId', numId);
  return fetchWithAuth(endpoint, 'DELETE');
};

export const setTenantVoiceNumberDefault = async (tenantId, numId, { op, purpose }) => {
  const endpoint = API_ROUTES.TENANT_VOICE_NUMBER_DEFAULTS_ADMIN
    .replace(':tenantId', tenantId)
    .replace(':numId', numId);
  return fetchWithAuth(endpoint, 'POST', { op, purpose });
};

// Voice Admin (Global PSTN & Settings)
export const getGlobalVoiceNumbers = async () => {
  try {
    return await fetchWithAuth(API_ROUTES.GLOBAL_VOICE_NUMBERS_ADMIN, 'GET', null, { silent: true });
  } catch (e) {
    const msg = String(e?.message || '');
    if (msg.includes('API Error (404)')) return { __unsupported: true };
    throw e;
  }
};

export const createGlobalVoiceNumber = async (payload) => {
  try {
    return await fetchWithAuth(API_ROUTES.GLOBAL_VOICE_NUMBERS_ADMIN, 'POST', payload, { silent: true });
  } catch (e) {
    const msg = String(e?.message || '');
    if (msg.includes('API Error (404)')) return { __unsupported: true };
    throw e;
  }
};

export const updateGlobalVoiceNumber = async (id, payload) => {
  const endpoint = `${API_ROUTES.GLOBAL_VOICE_NUMBERS_ADMIN}/${encodeURIComponent(id)}`;
  try {
    return await fetchWithAuth(endpoint, 'PATCH', payload, { silent: true });
  } catch (e) {
    const msg = String(e?.message || '');
    if (msg.includes('API Error (404)')) return { __unsupported: true };
    throw e;
  }
};

export const deleteGlobalVoiceNumber = async (id) => {
  const endpoint = `${API_ROUTES.GLOBAL_VOICE_NUMBERS_ADMIN}/${encodeURIComponent(id)}`;
  try {
    return await fetchWithAuth(endpoint, 'DELETE', null, { silent: true });
  } catch (e) {
    const msg = String(e?.message || '');
    if (msg.includes('API Error (404)')) return { __unsupported: true };
    throw e;
  }
};

export const toggleGlobalVoiceNumberStatus = async (id, status) => {
  const endpoint = `${API_ROUTES.GLOBAL_VOICE_NUMBERS_ADMIN}/${encodeURIComponent(id)}`;
  try {
    return await fetchWithAuth(endpoint, 'PATCH', { status }, { silent: true });
  } catch (e) {
    const msg = String(e?.message || '');
    if (msg.includes('API Error (404)')) return { __unsupported: true };
    throw e;
  }
};

export const getVoiceAdminSettings = async () => {
  try {
    return await fetchWithAuth(API_ROUTES.VOICE_SETTINGS_ADMIN, 'GET', null, { silent: true });
  } catch (e) {
    const msg = String(e?.message || '');
    if (msg.includes('API Error (404)')) return { __unsupported: true };
    throw e;
  }
};

export const updateVoiceAdminSettings = async (payload) => {
  try {
    return await fetchWithAuth(API_ROUTES.VOICE_SETTINGS_ADMIN, 'PATCH', payload, { silent: true });
  } catch (e) {
    const msg = String(e?.message || '');
    if (msg.includes('API Error (404)')) return { __unsupported: true };
    throw e;
  }
};

export const runVoiceHealthCheck = async () => {
  try {
    return await fetchWithAuth(API_ROUTES.VOICE_HEALTH_ADMIN, 'GET', null, { silent: true });
  } catch (e) {
    const msg = String(e?.message || '');
    if (msg.includes('API Error (404)')) return { __unsupported: true };
    throw e;
  }
};

export const getVoiceTelemetry = async (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v).length > 0) qs.set(k, v);
  });
  const endpoint = `${API_ROUTES.VOICE_TELEMETRY_ADMIN}${qs.toString() ? `?${qs.toString()}` : ''}`;
  try {
    return await fetchWithAuth(endpoint, 'GET', null, { silent: true });
  } catch (e) {
    const msg = String(e?.message || '');
    if (msg.includes('API Error (404)')) return { __unsupported: true, data: [] };
    throw e;
  }
};

// TwiML Dashboard (Admin)
export const getVoiceApps = async () => {
  const resp = await fetchWithAuth(API_ROUTES.VOICE_APPS_ADMIN, 'GET', null, { silent: true });
  return Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
};

export const getVoiceCalls = async () => {
  const resp = await fetchWithAuth(API_ROUTES.VOICE_CALLS_ADMIN, 'GET', null, { silent: true });
  return Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
};

export const getVoiceLogs = async (tenantId) => {
  const qs = new URLSearchParams();
  if (tenantId) qs.set('tenantId', tenantId);
  const url = `${API_ROUTES.VOICE_LOGS_ADMIN}${qs.toString() ? `?${qs.toString()}` : ''}`;
  const resp = await fetchWithAuth(url, 'GET', null, { silent: true });
  return Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
};

export const getVoiceIvrFlows = async () => {
  const resp = await fetchWithAuth(API_ROUTES.VOICE_IVR_ADMIN, 'GET', null, { silent: true });
  return Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
};

export const getVoiceAcdQueues = async () => {
  const resp = await fetchWithAuth(API_ROUTES.VOICE_ACD_ADMIN, 'GET', null, { silent: true });
  return Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
};

export const updateVoiceApp = async (appSid, payload) => {
  const path = API_ROUTES.VOICE_APP_ADMIN.replace(':appSid', encodeURIComponent(appSid));
  const resp = await fetchWithAuth(path, 'PATCH', payload, { silent: true });
  return resp?.data ?? resp;
};

export const createVoiceTestCall = async ({ to, from, message, type }) => {
  const body = { to, from, message, type };
  const resp = await fetchWithAuth(API_ROUTES.VOICE_TEST_CALL_ADMIN, 'POST', body, { silent: true });
  return resp?.data ?? resp;
};

export const playVoiceMessage = async ({ to, from, message }) => {
  const body = { to, from, message };
  const resp = await fetchWithAuth(API_ROUTES.VOICE_PLAY_MESSAGE_ADMIN, 'POST', body, { silent: true });
  return resp?.data ?? resp;
};

// ---------------------------------------------------------------------------
// Device Alert Delivery
// ---------------------------------------------------------------------------

export const acknowledgeAlertDelivery = async (eventId, tenantId, clientId) => {
  const url = `${API_ROUTES.ALERT_DELIVERY_ACK}/${encodeURIComponent(eventId)}/acknowledge`;
  const resp = await fetchWithAuth(url, 'POST', { tenantId, clientId }, { silent: true });
  return resp?.data ?? resp;
};

// ---------------------------------------------------------------------------
// Notification Response
// ---------------------------------------------------------------------------

/**
 * Submit a notification response button click.
 * Backend enforces idempotency (first response wins) and lifecycle state.
 *
 * @param {Object} params
 * @param {string} params.notificationId - Action ID that produced the notification
 * @param {string} params.responseKey    - Machine-readable key from responses[]
 * @param {string} [params.source]       - 'electron' | 'web' | 'mobile' (default: auto-detected)
 * @returns {Promise<Object>} - { success, accepted, ... } or error shape
 */
function waitForNotificationAuth(timeoutMs = 1500) {
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (user) => {
      if (settled || !user) return;
      settled = true;
      clearTimeout(timer);
      unsubscribe();
      resolve(user);
    };
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      unsubscribe();
      const error = new Error('Sign in to respond');
      error.status = 401;
      error.code = 'sign_in_required';
      reject(error);
    }, timeoutMs);
    const unsubscribe = onAuthStateChanged(auth, finish);
  });
}

export const sendNotificationResponse = async ({ notificationId, responseKey, source }) => {
  const detectedSource = source || (
    (typeof window !== 'undefined' && window.electronAPI) ? 'electron' : 'web'
  );
  const body = { notificationId, responseKey, source: detectedSource };
  await waitForNotificationAuth();
  try {
    return await fetchWithAuth(API_ROUTES.NOTIFICATION_RESPOND, 'POST', body);
  } catch (error) {
    if (!shouldRefreshExpiredToken(error, false)) throw error;
    return fetchWithAuth(API_ROUTES.NOTIFICATION_RESPOND, 'POST', body, { forceTokenRefresh: true });
  }
};

// ---------------------------------------------------------------------------
// Sites (Phase 1A)
// ---------------------------------------------------------------------------

/**
 * List all sites for the current tenant
 * @returns {Promise<Object>} - { success, data: Array<Site>, count }
 */
export const getSites = async (tenantId) => {
  const resp = await fetchWithAuth(
    buildUrlWithQuery(API_ROUTES.SITES_LIST, { tenantId }),
    'GET',
    null,
    { silent: true }
  );
  return resp;
};

/**
 * Get a single site by ID
 * @param {string} siteId - Site ID
 * @returns {Promise<Object>} - { success, data: Site }
 */
export const getSite = async (siteId, tenantId) => {
  const path = API_ROUTES.SITES_GET.replace(':siteId', encodeURIComponent(siteId));
  const resp = await fetchWithAuth(buildUrlWithQuery(path, { tenantId }), 'GET', null, { silent: true });
  return resp;
};

/**
 * Create a new site
 * @param {Object} siteData - Site data
 * @returns {Promise<Object>} - { success, data: Site, siteId }
 */
export const createSite = async (siteData, tenantId) => {
  const resp = await fetchWithAuth(
    API_ROUTES.SITES_CREATE,
    'POST',
    tenantId ? { ...siteData, tenantId } : siteData,
    { silent: true }
  );
  return resp;
};

/**
 * Update a site
 * @param {string} siteId - Site ID
 * @param {Object} updates - Site updates
 * @returns {Promise<Object>} - { success, data: Site }
 */
export const updateSite = async (siteId, updates, tenantId) => {
  const path = API_ROUTES.SITES_UPDATE.replace(':siteId', encodeURIComponent(siteId));
  const resp = await fetchWithAuth(
    path,
    'PUT',
    tenantId ? { ...updates, tenantId } : updates,
    { silent: true }
  );
  return resp;
};

/**
 * Delete a site
 * @param {string} siteId - Site ID
 * @returns {Promise<Object>} - { success, message }
 */
export const deleteSite = async (siteId, tenantId) => {
  const path = API_ROUTES.SITES_DELETE.replace(':siteId', encodeURIComponent(siteId));
  const resp = await fetchWithAuth(
    buildUrlWithQuery(path, { tenantId }),
    'DELETE',
    tenantId ? { tenantId } : null,
    { silent: true }
  );
  return resp;
};

// --- Embedded Agent Activation ---

export const previewActivationCode = async (activationCode) => {
  if (!activationCode) throw new Error('activationCode required');
  const code = String(activationCode).trim().toUpperCase();
  return fetchWithAuth(`/api/v2/admin/activation/${code}`, 'GET', null, { silent: true });
};

export const claimActivationCode = async (activationCode, tenantId) => {
  if (!activationCode || !tenantId) throw new Error('activationCode and tenantId required');
  const code = String(activationCode).trim().toUpperCase();
  return fetchWithAuth(
    `/api/v2/admin/activation/${code}/claim`,
    'POST',
    { tenantId }
  );
};

// --- Collaboration Workspaces (standalone) ---

export const listCollaborationWorkspaces = async (tenantId) => {
  if (!tenantId) return [];
  const resp = await fetchWithAuth(
    `/api/v2/tenants/${tenantId}/collaboration-workspaces`,
    'GET',
    null,
    { silent: true }
  );
  return resp?.workspaces || [];
};

export const getCollaborationWorkspace = async (tenantId, workspaceId) => {
  if (!tenantId || !workspaceId) return null;
  const resp = await fetchWithAuth(
    `/api/v2/tenants/${tenantId}/collaboration-workspaces/${workspaceId}`,
    'GET',
    null,
    { silent: true }
  );
  return resp?.workspace || null;
};

export const createCollaborationWorkspace = async (tenantId, data) => {
  if (!tenantId) throw new Error('tenantId required');
  return fetchWithAuth(
    `/api/v2/tenants/${tenantId}/collaboration-workspaces`,
    'POST',
    data
  );
};

export const updateCollaborationWorkspace = async (tenantId, workspaceId, data) => {
  if (!tenantId || !workspaceId) throw new Error('tenantId and workspaceId required');
  return fetchWithAuth(
    `/api/v2/tenants/${tenantId}/collaboration-workspaces/${workspaceId}`,
    'PUT',
    data
  );
};

export const deleteCollaborationWorkspace = async (tenantId, workspaceId) => {
  if (!tenantId || !workspaceId) throw new Error('tenantId and workspaceId required');
  return fetchWithAuth(
    `/api/v2/tenants/${tenantId}/collaboration-workspaces/${workspaceId}`,
    'DELETE'
  );
};

export const createCallRoutingRule = async (tenantId, payload) => {
  if (!tenantId) throw new Error('tenantId required');
  return fetchWithAuth(`/api/v2/tenants/${tenantId}/call-routing`, 'POST', payload);
};

export const updateCallRoutingRule = async (tenantId, routeId, payload) => {
  if (!tenantId || !routeId) throw new Error('tenantId and routeId required');
  return fetchWithAuth(`/api/v2/tenants/${tenantId}/call-routing/${routeId}`, 'PUT', payload);
};

export const deleteCallRoutingRule = async (tenantId, routeId) => {
  if (!tenantId || !routeId) throw new Error('tenantId and routeId required');
  return fetchWithAuth(`/api/v2/tenants/${tenantId}/call-routing/${routeId}`, 'DELETE');
};

// --- Contact Lists ---

export const listContactLists = async (tenantId, { includeInactive = false } = {}) => {
  if (!tenantId) return [];
  const params = includeInactive ? '?includeInactive=true' : '';
  const resp = await fetchWithAuth(
    `/api/v2/tenants/${tenantId}/contact-lists${params}`,
    'GET',
    null,
    { silent: true }
  );
  return resp?.data?.contactLists || resp?.contactLists || [];
};

export const getContactList = async (tenantId, listId) => {
  if (!tenantId || !listId) throw new Error('tenantId and listId required');
  return fetchWithAuth(`/api/v2/tenants/${tenantId}/contact-lists/${listId}`);
};

export const createContactList = async (tenantId, payload) => {
  if (!tenantId) throw new Error('tenantId required');
  return fetchWithAuth(`/api/v2/tenants/${tenantId}/contact-lists`, 'POST', payload);
};

export const updateContactList = async (tenantId, listId, payload) => {
  if (!tenantId || !listId) throw new Error('tenantId and listId required');
  return fetchWithAuth(`/api/v2/tenants/${tenantId}/contact-lists/${listId}`, 'PUT', payload);
};

export const deleteContactList = async (tenantId, listId, { hard = false } = {}) => {
  if (!tenantId || !listId) throw new Error('tenantId and listId required');
  const qs = hard ? '?hard=true' : '';
  return fetchWithAuth(`/api/v2/tenants/${tenantId}/contact-lists/${listId}${qs}`, 'DELETE');
};

export const addContactListMember = async (tenantId, listId, member) => {
  if (!tenantId || !listId) throw new Error('tenantId and listId required');
  return fetchWithAuth(`/api/v2/tenants/${tenantId}/contact-lists/${listId}/members`, 'POST', member);
};

export const removeContactListMember = async (tenantId, listId, memberId) => {
  if (!tenantId || !listId || !memberId) throw new Error('tenantId, listId, and memberId required');
  return fetchWithAuth(
    `/api/v2/tenants/${tenantId}/contact-lists/${listId}/members/${memberId}`,
    'DELETE'
  );
};

export const updateContactListMember = async (tenantId, listId, memberId, patch) => {
  if (!tenantId || !listId || !memberId) throw new Error('tenantId, listId, and memberId required');
  return fetchWithAuth(
    `/api/v2/tenants/${tenantId}/contact-lists/${listId}/members/${memberId}`,
    'PATCH',
    patch
  );
};

export const getTenantProfile = (tenantId) => {
  const endpoint = API_ROUTES.ADMIN_TENANT_PROFILE.replace(':tenantId', encodeURIComponent(tenantId));
  return fetchWithAuth(endpoint, 'GET', null, { silent: true });
};

export const getTenantSupport = (tenantId, { limit = 25, cursor = '' } = {}) => {
  const endpoint = API_ROUTES.ADMIN_TENANT_SUPPORT.replace(':tenantId', encodeURIComponent(tenantId));
  const params = new URLSearchParams();
  if (limit) params.set('limit', String(limit));
  if (cursor) params.set('cursor', cursor);
  const query = params.toString();
  return fetchWithAuth(query ? `${endpoint}?${query}` : endpoint, 'GET', null, { silent: true });
};

export const getTenantUsage = (tenantId) => {
  const endpoint = API_ROUTES.ADMIN_TENANT_USAGE.replace(':tenantId', encodeURIComponent(tenantId));
  return fetchWithAuth(endpoint, 'GET', null, { silent: true });
};

const tenantDeletionPath = (route, tenantId, jobId) =>
  route
    .replace(':tenantId', encodeURIComponent(tenantId))
    .replace(':jobId', encodeURIComponent(jobId || ''));

export const getTenantDeletionInventory = (tenantId) =>
  fetchWithAuth(tenantDeletionPath(API_ROUTES.ADMIN_TENANT_DELETION_INVENTORY, tenantId), 'GET', null, {
    silent: true,
  });

export const getTenantDeletionArchive = (tenantId) =>
  fetchWithAuth(tenantDeletionPath(API_ROUTES.ADMIN_TENANT_DELETION_ARCHIVE, tenantId), 'GET', null, {
    silent: true,
  });

export const startTenantDeletion = (tenantId, { confirmName, reason, permanent }) =>
  fetchWithAuth(
    tenantDeletionPath(API_ROUTES.ADMIN_TENANT_DELETION_START, tenantId),
    'POST',
    { confirmName, reason, permanent: permanent === true },
    { silent: true }
  );

export const getTenantDeletionJob = (tenantId, jobId) =>
  fetchWithAuth(tenantDeletionPath(API_ROUTES.ADMIN_TENANT_DELETION_JOB, tenantId, jobId), 'GET', null, {
    silent: true,
  });

export const retryTenantDeletion = (tenantId, jobId) =>
  fetchWithAuth(
    tenantDeletionPath(API_ROUTES.ADMIN_TENANT_DELETION_RETRY, tenantId, jobId),
    'POST',
    {},
    { silent: true }
  );

export const getGoogleBillingSummary = (month) =>
  fetchWithAuth(buildUrlWithQuery(API_ROUTES.ADMIN_BILLING_GOOGLE_SUMMARY, { month }), 'GET', null, {
    silent: true,
  });

export const getTwilioBillingSummary = (month) =>
  fetchWithAuth(buildUrlWithQuery(API_ROUTES.ADMIN_BILLING_TWILIO_SUMMARY, { month }), 'GET', null, {
    silent: true,
  });

export const getTwilioBillingTenants = (month) =>
  fetchWithAuth(buildUrlWithQuery(API_ROUTES.ADMIN_BILLING_TWILIO_TENANTS, { month }), 'GET', null, {
    silent: true,
  });

export const getTwilioBillingItems = (params = {}) =>
  fetchWithAuth(buildUrlWithQuery(API_ROUTES.ADMIN_BILLING_TWILIO_ITEMS, params), 'GET', null, {
    silent: true,
  });

export const getPricingCatalog = () =>
  fetchWithAuth(API_ROUTES.ADMIN_PRICING_CATALOG, 'GET', null, { silent: true });

export const createPricingFeature = (body) =>
  fetchWithAuth(API_ROUTES.ADMIN_PRICING_CATALOG, 'POST', body, { silent: true });

export const initializePricingCatalog = () =>
  fetchWithAuth(API_ROUTES.ADMIN_PRICING_INITIALIZE, 'POST', { confirm: true }, { silent: true });

export const updatePricingFeature = (featureId, body) =>
  fetchWithAuth(API_ROUTES.ADMIN_PRICING_FEATURE.replace(':featureId', encodeURIComponent(featureId)), 'PATCH', body, {
    silent: true,
  });

async function readPricingResponse(response) {
  const text = await response.text();
  let payload = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }
  if (!response.ok) {
    const error = new Error(payload?.message || `API Error (${response.status})`);
    error.status = response.status;
    error.code = payload?.error || '';
    throw error;
  }
  return payload;
}

function saveWorkbookFile(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export const downloadPricingWorkbook = async () => {
  const token = await getIdToken();
  if (!token) throw new Error('User not authenticated');
  const response = await fetch(backendApiUrl(API_ROUTES.ADMIN_PRICING_WORKBOOK), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    await readPricingResponse(response);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="([^"]+)"/);
  saveWorkbookFile(blob, match?.[1] || 'SignalBridge-Wholesale-Catalog.xlsx');
};

export const previewPricingWorkbook = async (file) => {
  const token = await getIdToken();
  if (!token) throw new Error('User not authenticated');
  const body = new FormData();
  body.append('file', file, file.name);
  const response = await fetch(backendApiUrl(API_ROUTES.ADMIN_PRICING_WORKBOOK_PREVIEW), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body,
  });
  return readPricingResponse(response);
};

export const applyPricingWorkbook = (previewId) =>
  fetchWithAuth(API_ROUTES.ADMIN_PRICING_WORKBOOK_APPLY, 'POST', { previewId }, { silent: true });

const dealerPath = (template, params) =>
  Object.entries(params).reduce(
    (path, [key, value]) => path.replace(`:${key}`, encodeURIComponent(value)),
    template
  );

export const listDealers = () => fetchWithAuth(API_ROUTES.ADMIN_DEALERS, 'GET', null, { silent: true });

export const createDealer = (body) => fetchWithAuth(API_ROUTES.ADMIN_DEALERS, 'POST', body, { silent: true });

export const getDealer = (dealerId) =>
  fetchWithAuth(dealerPath(API_ROUTES.ADMIN_DEALER, { dealerId }), 'GET', null, { silent: true });

export const updateDealer = (dealerId, body) =>
  fetchWithAuth(dealerPath(API_ROUTES.ADMIN_DEALER, { dealerId }), 'PATCH', body, { silent: true });

export const listDealerAdmins = (dealerId) =>
  fetchWithAuth(dealerPath(API_ROUTES.ADMIN_DEALER_ADMINS, { dealerId }), 'GET', null, { silent: true });

export const assignDealerAdmin = (dealerId, userId) =>
  fetchWithAuth(dealerPath(API_ROUTES.ADMIN_DEALER_ADMINS, { dealerId }), 'POST', { userId }, { silent: true });

export const updateDealerAdmin = (dealerId, userId, status) =>
  fetchWithAuth(dealerPath(API_ROUTES.ADMIN_DEALER_ADMIN, { dealerId, userId }), 'PATCH', { status }, { silent: true });

export const updateDealerAdminAlgo = (dealerId, userId, tenantId, algoDeviceSupport) =>
  fetchWithAuth(
    `${dealerPath(API_ROUTES.ADMIN_DEALER_ADMIN, { dealerId, userId })}/algo-device-support`,
    'PATCH',
    { tenantId, algoDeviceSupport: algoDeviceSupport === true },
    { silent: true },
  );

export const listDealerAssignments = (dealerId) =>
  fetchWithAuth(dealerPath(API_ROUTES.ADMIN_DEALER_TENANTS, { dealerId }), 'GET', null, { silent: true });

export const listAllDealerAssignments = () =>
  fetchWithAuth(API_ROUTES.ADMIN_DEALER_ASSIGNMENTS, 'GET', null, { silent: true });

export const assignDealerTenant = (dealerId, tenantId) =>
  fetchWithAuth(dealerPath(API_ROUTES.ADMIN_DEALER_TENANTS, { dealerId }), 'POST', { tenantId }, { silent: true });

export const disableDealerTenant = (dealerId, tenantId) =>
  fetchWithAuth(dealerPath(API_ROUTES.ADMIN_DEALER_TENANT, { dealerId, tenantId }), 'DELETE', null, { silent: true });

export const getDealerContexts = () =>
  fetchWithAuth(API_ROUTES.DEALER_CONTEXTS, 'GET', null, { silent: true });

export const getDealerTenants = (dealerId) =>
  fetchWithAuth(dealerPath(API_ROUTES.DEALER_TENANTS, { dealerId }), 'GET', null, { silent: true });

export const getDealerTenantOverview = (dealerId, tenantId) =>
  fetchWithAuth(dealerPath(API_ROUTES.DEALER_TENANT, { dealerId, tenantId }), 'GET', null, { silent: true });

export const getDealerTenantSupport = (dealerId, tenantId) =>
  fetchWithAuth(dealerPath(API_ROUTES.DEALER_TENANT_SUPPORT, { dealerId, tenantId }), 'GET', null, { silent: true });

export const getDealerSubscription = (dealerId, tenantId) =>
  fetchWithAuth(dealerPath(API_ROUTES.DEALER_SUBSCRIPTION, { dealerId, tenantId }), 'GET', null, { silent: true });

export const previewDealerSubscription = (dealerId, tenantId, body) =>
  fetchWithAuth(dealerPath(API_ROUTES.DEALER_SUBSCRIPTION_PREVIEW, { dealerId, tenantId }), 'POST', body, { silent: true });

export const confirmDealerSubscription = (dealerId, tenantId, body) =>
  fetchWithAuth(dealerPath(API_ROUTES.DEALER_SUBSCRIPTION, { dealerId, tenantId }), 'PUT', body, { silent: true });

export const getCustomerSubscription = (tenantId) =>
  fetchWithAuth(dealerPath(API_ROUTES.TENANT_SUBSCRIPTION, { tenantId }), 'GET', null, { silent: true });

export const getDealerUsage = (dealerId, tenantId, currency) => {
  const params = new URLSearchParams({ currency });
  if (tenantId) params.set('tenantId', tenantId);
  return fetchWithAuth(`${dealerPath(API_ROUTES.DEALER_USAGE, { dealerId })}?${params.toString()}`, 'GET', null, { silent: true });
};

export const getCustomerUsage = (tenantId, currency) =>
  fetchWithAuth(`${dealerPath(API_ROUTES.TENANT_SUBSCRIPTION_USAGE, { tenantId })}?currency=${encodeURIComponent(currency)}`, 'GET', null, { silent: true });

export const getPricingUsage = (query) =>
  fetchWithAuth(`${API_ROUTES.ADMIN_PRICING_USAGE}${query || ''}`, 'GET', null, { silent: true });

export const getCommercialBeta = () =>
  fetchWithAuth(API_ROUTES.ADMIN_PRICING_BETA, 'GET', null, { silent: true });

export const setCommercialBeta = (body) =>
  fetchWithAuth(API_ROUTES.ADMIN_PRICING_BETA, 'POST', body, { silent: true });
